import React, { useState, useEffect, useContext, Suspense } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PlusIcon } from "@heroicons/react/solid";
import _isEmpty from "lodash/isEmpty";
import _omit from "lodash/omit";
import _cloneDeep from "lodash/cloneDeep";
import _sortBy from "lodash/sortBy";
import Cookies from "js-cookie";
import { useHistory } from "react-router-dom";

import ModalWrapper from "components/lib/Shared/ModalWrapper";
import CompanyOfficialSelection from "./CompanyOfficialSelection";
import CompanyOfficialsTable from "./CompanyOfficialsTable";
import options from "../SFLdata/selectOptions";
import { openModal } from "state/slices/modals";
import {
  flattenObject,
  getFullName,
  getMaxCompanySecretariesLimit,
} from "utils";
import { resetSharesTable } from "state/slices/tables/sharesAllocationTable";
import ErrorBanner from "components/lib/Shared/ErrorBanner";
import { checkForCompaniesRegistrar } from "utils/company";
import Button from "components/lib/Shared/Button";
import { Constants } from "config/constants";
import { setCurrentJSGroupID } from "state/slices/tables/jointShareholderTable";
import { validateCompanyOfficialsAsync } from "state/slices/company";
import Overlay from "components/lib/Shared/Overlay";
import ChangesContext from "context/ChangesContext";
import TransactionEditContext from "context/TransactionEditContext";
import CompanyDashboardContext from "context/CompanyDashboardContext";
import UpdateHistoryContext from "context/UpdateHistoryContext";
import ManualTransactionReviewContext from "context/ManualTransactionReviewContext";

// Error Boundary Component
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-100 border border-red-400 rounded">
          <h2 className="text-red-700">Something went wrong!</h2>
          <p className="text-red-600">Please try reloading the page.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const CompanyOfficialsTab = (props) => {
  const { handleBackBtn, handleNextBtn, submitBtnLabel, isSubmitting } =
    props || {};
  const history = useHistory();
  const role = Cookies.get("role");
  // ||
  //   isInChangesModule ||
  //   isInTransactionEditContext
  const { isInChangesModule } = useContext(ChangesContext) || {};
  const { isInTransactionEditContext } =
    useContext(TransactionEditContext) || {};
  const [showCompanyOfficialsForm, setShowCompanyOfficialsForm] = useState(
    isInChangesModule || isInTransactionEditContext ? true : false
  );
  const [selectedOfficial, setSelectedOfficial] = useState(
    props.selectedOfficial
  );
  const [mode, setMode] = useState({
    editing: !!selectedOfficial,
    id: selectedOfficial?._id || "",
    viewing: false,
  });
  const [changingShareholderDesignation, setChangingShareholderDesignation] =
    useState(false);
  const { isInUpdateHistoryContext, selectionsMade, transactions } =
    useContext(UpdateHistoryContext) || {};
  const { initiatedTransactions, getInitiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  let stakeholderDetails = selectionsMade?.stakeholderDetails || {};
  let changeOfParticularsDesignations =
    stakeholderDetails?.changeOfParticulars || [];
  let cessationDesignations = stakeholderDetails?.cessation || [];
  let updateAppointmentDesignations = stakeholderDetails?.appointment || [];
  updateAppointmentDesignations = updateAppointmentDesignations.map(
    (designation) => {
      if (designation === Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER) {
        designation = Constants.APPLICANT_CAPACITIES.DIRECTOR;
      }
      return designation;
    }
  );
  if (
    cessationDesignations?.length > 0 &&
    cessationDesignations?.includes(
      Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER
    )
  ) {
    updateAppointmentDesignations.push(
      Constants.APPLICANT_CAPACITIES.SHAREHOLDER
    );
  }
  let selectedDesignations = new Set([
    ...changeOfParticularsDesignations,
    ...cessationDesignations,
  ]);
  const appointmentDesignations = stakeholderDetails?.appointment;
  const canAppointDirectorShareholder = () => {
    if (appointmentDesignations && appointmentDesignations?.length > 0) {
      return appointmentDesignations.includes(
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER
      );
    }
    return false;
  };

  const canCeaseDirectorShareholder = () => {
    if (cessationDesignations && cessationDesignations?.length > 0) {
      return cessationDesignations.includes(
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER
      );
    }
    return false;
  };

  if (canAppointDirectorShareholder()) {
    selectedDesignations.add(Constants.APPLICANT_CAPACITIES.SHAREHOLDER);
  }
  if (canCeaseDirectorShareholder()) {
    selectedDesignations.add(
      Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER
    );
  }
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [formStepErrors, setFormStepErrors] = useState([]);
  const [showSelectRegistrarBtn, setShowSelectRegistrarBtn] = useState(false);
  const [registrarCandidates, setRegistrarCandidates] = useState([]);
  const [assigningRegistrar, setAssigningRegistrar] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [hasSpecificShareholderOffer, setHasSpecificShareholderOffer] =
    useState(false);
  const [hasShareTransfer, sethasShareTransfer] = useState(false);

  const dispatch = useDispatch();
  const companyDashboardSlice = useSelector(
    (state) => state.companyDashboardSlice
  );
  const companyOfficialsSlice =
    useSelector((state) => state.companyOfficialsSlice) || {};
  const originalCompanyOfficials =
    companyOfficialsSlice.companyOfficials?.items || [];
  const { getCompanyOfficialsStatus } = companyOfficialsSlice || {};
  const { jointShareholders, currentJSGroupID } =
    useSelector((state) => state.jointShareholderTableSlice || {}) || {};
  const count = jointShareholders?.length || 0;

  let companyOfficials = originalCompanyOfficials;

  const [updateData, setUpdateData] = useState([]);
  const [changesListData, setChangesListData] = useState([]);

  const JSGroups = new Set();
  companyOfficials.forEach((item) => {
    if (item?.JSGroupID) JSGroups.add(item.JSGroupID);
  });

  const userSlice = useSelector((state) => state.userSlice) || {};
  const { company, validateCompanyOfficialsStatus } =
    useSelector((state) => state.companySlice || {}) || {};
  const { beneficialOwners } =
    useSelector((state) => state.beneficialOwnersTableSlice || {}) || {};
  const beneficialOwnersDropdown = (beneficialOwners || []).filter(
    (bo) =>
      bo?.showInBoDropdown &&
      [
        bo?.electronicAddress?.emailAddress,
        bo?.identificationDetails?.guardianEmailAddress,
      ].includes(userSlice?.electronicAddress?.emailAddress)
  );
  const numberOfBoOfficials = beneficialOwnersDropdown?.length ?? 0;
  const isApplicant = company?.createdBy?._id === userSlice?._id;

  const enableBoTab =
    beneficialOwners?.find(
      (bo) =>
        bo?.electronicAddress?.emailAddress &&
        [
          bo?.electronicAddress?.emailAddress,
          bo?.identificationDetails?.guardianEmailAddress,
        ].includes(userSlice?.electronicAddress?.emailAddress)
    ) || numberOfBoOfficials > 0;

  const data = getDataWithJS(companyOfficials, JSGroups);
  // Filter out any falsy values

  const toggleShowOfficialsForm = (show = false) => {
    if (show) {
      setMode({ editing: false, id: "", designation: null, viewing: false });
      setShowCompanyOfficialsForm(true);
    } else {
      setSelectedOfficial(null);
      setShowCompanyOfficialsForm(show);
      setChangingShareholderDesignation(false);
    }
  };

  const handleAddCompanyOfficial = () => {
    setMode({ editing: false, id: "", designation: null, viewing: false });
    setShowCompanyOfficialsForm(true);
    dispatch(setCurrentJSGroupID(""));
  };

  const handleCOEdit = (row) => {
    if (!row?.isViewOnly) setIsViewOnly(false);
    else setIsViewOnly(false);
    setMode({ editing: true, id: row?._id, viewing: false });
    dispatch(openModal({ modalName: "companyOfficial_edit_modal" }));
  };

  const handleView = (row) => {
    setMode({ editing: true, id: row?._id, viewing: true });
    setIsViewOnly(true);
    dispatch(openModal({ modalName: "companyOfficial_edit_modal" }));
  };

  useEffect(() => {
    if (props?.setAddNewOfficialSelected) {
      props.setAddNewOfficialSelected(showCompanyOfficialsForm);
    }
  }, [showCompanyOfficialsForm]);

  useEffect(() => {
    if (originalCompanyOfficials.length > 0) {
      if (initiatedTransactions.items?.length > 0) {
        // Check for specificShareholderOffer option in transactions
        const hasSpecificOffer = initiatedTransactions.items.some(
          (trx) =>
            trx?.transactionType === Constants.TRANSACTION_TYPES.MANUAL &&
            trx?.shareCapitalChanges?.some(
              (sc) => sc?.option === "specificShareholderOffer"
            )
        );

        setHasSpecificShareholderOffer(hasSpecificOffer);

        const isShareTransfer = initiatedTransactions.items.some(
          (trx) => trx?.processType === "shareTransfer"
        );

        sethasShareTransfer(isShareTransfer);
      }

      setUpdateDataAndChangesListData({
        originalCompanyOfficials,
        transactions: initiatedTransactions.items,
        data,
        selectedDesignations,
        setUpdateData,
        setChangesListData,
        JSGroups,
        selectionsMade,
      });
    }
  }, [initiatedTransactions, originalCompanyOfficials]);

  useEffect(() => {
    if (selectedOfficial?.idType) {
      setMode({ ...mode, editing: true, id: selectedOfficial._id });
    }
  }, [selectedOfficial]);

  useEffect(() => {
    if (currentJSGroupID || count > 0) {
      toggleShowOfficialsForm(true);
    } else dispatch(setCurrentJSGroupID(""));
  }, [count, currentJSGroupID]);

  useEffect(() => {
    return () => {
      dispatch(resetSharesTable());
    };
  }, [dispatch]);

  const validateTab = async (goToNextStep = false) => {
    setFormStepErrors([]);
    setIsValidating(true);
    const response = await dispatch(
      validateCompanyOfficialsAsync({
        companyId: company?._id,
      })
    );
    const errors = response.payload?.errors ?? [];
    setIsValidating(false);
    if (errors.length !== 0) setFormStepErrors(errors);
    else if (goToNextStep) handleNextBtn?.();
  };

  useEffect(() => {
    if (isApplicant) {
      const { showSelectRegistrarBtn, registrarCandidates = [] } =
        checkForCompaniesRegistrar({
          type: company?.type,
          officials: originalCompanyOfficials,
        }) || {};
      setShowSelectRegistrarBtn(!!showSelectRegistrarBtn);
      setRegistrarCandidates(registrarCandidates);
    }
  }, [originalCompanyOfficials, company?.type, isApplicant]);

  const handleSubmit = () => {
    if (role === Constants.COOKIE_ROLES.OFFICIAL) {
      if (enableBoTab && company?.status === "Draft") {
        handleNextBtn?.();
      } else history.push("/transactions");
      return;
    }
    validateTab(true);
  };

  const numberOfCompanySecretaries = data.filter(
    (official) => official?.designation === "Company Secretary"
  ).length;

  let companyOfficialOptions = [
    ...options.companyOfficials.filter(
      (official) => official.value !== "Company Secretary"
    ),
    {
      value: "Company Secretary",
      label: "Company Secretary",
      disabled:
        numberOfCompanySecretaries ===
        getMaxCompanySecretariesLimit(company?.type),
    },
  ];

  if (updateAppointmentDesignations?.length > 0) {
    companyOfficialOptions = companyOfficialOptions.filter((option) =>
      updateAppointmentDesignations.includes(option.value)
    );
  } else if (hasSpecificShareholderOffer || hasShareTransfer) {
    // Filter to only show shareholder-related designations for specificShareholderOffer transactions
    const allowedDesignations = [
      "Shareholder",
      "Director Shareholder",
      "Joint Shareholder",
    ];
    companyOfficialOptions = companyOfficialOptions.filter((option) =>
      allowedDesignations.includes(option.value)
    );
  }

  return (
    <ErrorBoundary>
      <div className="relative">
        <ErrorBanner errors={formStepErrors} className="mb-2" />
        {getCompanyOfficialsStatus?.status === "loading" && <Overlay />}
        {!isInChangesModule &&
          !isInTransactionEditContext &&
          !showCompanyOfficialsForm &&
          data.length === 0 && (
            <div className="space-y-2">
              <div className="inline-flex px-6 py-3 bg-white rounded shadow-sm">
                <p className="text-gray-800">
                  Your company has no officials listed. Please click the{" "}
                  <b className="text-secondary">+ Add company officials</b> to
                  add appointments.
                </p>
              </div>
            </div>
          )}
        {!isInChangesModule &&
          !isInTransactionEditContext &&
          !showCompanyOfficialsForm && (
            <>
              {showSelectRegistrarBtn && !assigningRegistrar && (
                <div className="flex justify-end">
                  <Button onClick={() => setAssigningRegistrar(true)}>
                    Assign Registrar
                  </Button>
                </div>
              )}
              <CompanyOfficialsTable
                assigningRegistrar={assigningRegistrar}
                registrarCandidates={registrarCandidates}
                toggleShowOfficialsForm={toggleShowOfficialsForm}
                handleEdit={handleCOEdit}
                handleView={handleView}
                mode={mode}
                data={isInUpdateHistoryContext ? updateData : data}
                setSelectedOfficial={setSelectedOfficial}
                setShowCompanyOfficialsForm={setShowCompanyOfficialsForm}
                setChangingShareholderDesignation={
                  setChangingShareholderDesignation
                }
                setMode={setMode}
              />
            </>
          )}
        {isInUpdateHistoryContext && changesListData.length > 0 && (
          <CompanyOfficialsTable
            assigningRegistrar={assigningRegistrar}
            registrarCandidates={registrarCandidates}
            toggleShowOfficialsForm={toggleShowOfficialsForm}
            handleEdit={handleCOEdit}
            handleView={handleView}
            mode={mode}
            data={changesListData}
            isChangesList={true}
          />
        )}
        {((isInUpdateHistoryContext &&
          (updateAppointmentDesignations?.length > 0 ||
            hasShareTransfer ||
            hasSpecificShareholderOffer) &&
          !showCompanyOfficialsForm) ||
          (!isInChangesModule &&
            !isInTransactionEditContext &&
            !isInUpdateHistoryContext &&
            !showCompanyOfficialsForm &&
            role !== Constants.COOKIE_ROLES.OFFICIAL)) && (
          <div className="mt-4 d-flex justify-content-end">
            <Button
              variant="text"
              onClick={handleAddCompanyOfficial}
              className="inline-flex items-center justify-center space-x-2 text-sm font-medium rounded-md text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mt-4"
            >
              <PlusIcon className="w-6 h-6" aria-hidden="true" />
              <span>Add Company Officials</span>
            </Button>
          </div>
        )}
        {mode.editing && !changingShareholderDesignation && (
          <ModalWrapper
            name="companyOfficial_edit_modal"
            title={
              isViewOnly ? "View Company Official" : "Edit Company Official"
            }
            size="lg"
            maxWidth="sm:max-w-fit"
            handleClose={toggleShowOfficialsForm}
          >
            <CompanyOfficialSelection
              companyOfficialOptions={companyOfficialOptions}
              toggleShowOfficialsForm={toggleShowOfficialsForm}
              mode={mode}
              component={CompanyOfficialSelection}
              isViewOnly={isViewOnly}
              selectedCompanyOfficial={selectedOfficial}
              setIsViewOnly={setIsViewOnly}
              handleBackBtn={handleBackBtn}
            />
          </ModalWrapper>
        )}
        {showCompanyOfficialsForm && (
          <CompanyOfficialSelection
            companyOfficialOptions={companyOfficialOptions}
            toggleShowOfficialsForm={toggleShowOfficialsForm}
            mode={mode}
            isViewOnly={isViewOnly}
            setMode={setMode}
            handleBackBtn={handleBackBtn}
            setIsViewOnly={setIsViewOnly}
            selectedCompanyOfficial={selectedOfficial}
            changingShareholderDesignation={changingShareholderDesignation}
          />
        )}
        {!isInChangesModule &&
          !isInTransactionEditContext &&
          !showCompanyOfficialsForm &&
          !isInUpdateHistoryContext && (
            <div className="flex justify-between py-3">
              <Button
                onClick={handleBackBtn}
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-600 border border-transparent rounded-md shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Go back
              </Button>
              <Button
                type="submit"
                isLoading={isSubmitting || isValidating}
                onClick={handleSubmit}
                loadingText={isValidating ? "Validating..." : "Submitting..."}
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:cursor-not-allowed"
              >
                {submitBtnLabel}
              </Button>
            </div>
          )}
      </div>
    </ErrorBoundary>
  );
};

export default CompanyOfficialsTab;

function getDataWithJS(officialsList, JSGroups) {
  if (officialsList?.length > 0) {
    return [
      ...officialsList.filter(
        (item) =>
          (item?.designation === "Joint Shareholder" &&
            item?.isDirector === "Yes") ||
          item?.designation !== "Joint Shareholder"
      ),
      ...getJointShareholders(officialsList, JSGroups).filter(
        (item) => !_isEmpty(item)
      ),
    ].filter(Boolean);
  }
  return [];
}

function getJointShareholders(officialsList, JSGroups) {
  const allJS = [...JSGroups].map((id) => {
    const names = [];
    let combinedJS = {};
    const currentGroupOfficials = [];
    officialsList.forEach((official) => {
      if (official?.JSGroupID === id) currentGroupOfficials.push(official);
    });
    const sortedJsOfficials = _sortBy(currentGroupOfficials, ["JSGroupOrder"]);
    sortedJsOfficials.forEach((official) => {
      if (official?.JSGroupID === id) {
        if (official?.names?.firstName || official?.names?.lastName) {
          names.push(getFullName(official.names));
        } else {
          names.push(getFullName(official));
        }

        if (official?.sharesAllotted?.length > 0)
          combinedJS.sharesAllotted = official.sharesAllotted;
        combinedJS = {
          ...combinedJS,
          ..._omit(_cloneDeep(official), ["sharesAllotted"]),
          designation: "Joint Shareholder",
          idType: "Joint Shareholder",
        };
        combinedJS.fullName = names.map((name, index) => {
          if (names.length === 2 && index === 0) return `${name} & `;
          if (names.length > 2 && index === names.length - 2)
            return `${name} & `;
          if (
            names.length > 2 &&
            index !== names.length - 2 &&
            index !== names.length - 1
          )
            return `${name}, `;
          return name;
        });
      }
    });
    return combinedJS;
  });
  return allJS;
}

function setUpdateDataAndChangesListData({
  originalCompanyOfficials,
  transactions,
  data,
  selectedDesignations,
  setUpdateData,
  setChangesListData,
  JSGroups,
  selectionsMade,
}) {
  if (originalCompanyOfficials?.length >= 0) {
    const shareInformation = selectionsMade?.shareInformation;
    const shareInfoToIncludeStakeholders = () => {
      if (shareInformation) {
        let capitalIncrease = shareInformation?.capitalIncrease;
        let capitalReduction = shareInformation?.capitalReduction;
        let sharesManagement = shareInformation?.sharesManagement;
        let shareholdingChanges = shareInformation?.shareholdingChanges;
        return (
          capitalIncrease?.includes("privateWithMoreThanOneClass") ||
          capitalIncrease?.includes("PVTwithOneClass") ||
          capitalIncrease?.includes("existingAuth") ||
          capitalIncrease?.includes("unallotedShares") ||
          capitalReduction?.length > 0 ||
          sharesManagement?.includes("sharesConsolidation") ||
          sharesManagement?.includes("sharesSubDivision") ||
          shareholdingChanges?.length > 0
        );
      }
    };

    if (transactions?.length > 0) {
      const transactionOfficials = getTransactionOfficials(
        transactions,
        originalCompanyOfficials
      );
      const filteredOfficials = getFilteredOfficials({
        data,
        transactionOfficials,
        selectedDesignations,
      });

      if (transactionOfficials.length > 0) {
        setUpdateData(getDataWithJS(filteredOfficials, JSGroups));
        setChangesListData(getDataWithJS(transactionOfficials, JSGroups));
      } else {
        if (shareInfoToIncludeStakeholders()) {
          setUpdateData(data);
        } else {
          if (selectedDesignations.size > 0) {
            setUpdateData(
              data.filter((official) =>
                selectedDesignations.has(official.designation)
              )
            );
          }
        }
        setChangesListData([]);
      }
    } else {
      if (shareInfoToIncludeStakeholders()) {
        setUpdateData(data);
      } else {
        if (selectedDesignations.size > 0) {
          setUpdateData(
            data.filter((official) =>
              selectedDesignations.has(official.designation)
            )
          );
        }
      }
      setChangesListData([]);
    }
  }
}

function getFilteredOfficials({
  data,
  transactionOfficials,
  selectedDesignations,
}) {
  let filteredOfficials = data.filter(
    (official) =>
      !transactionOfficials.some(
        (trxOfficial) => trxOfficial?._id === official?._id
      )
  );
  if (selectedDesignations.size > 0) {
    filteredOfficials = filteredOfficials.filter((official) =>
      selectedDesignations.has(official?.designation)
    );
  } else {
    filteredOfficials = filteredOfficials;
  }
  return filteredOfficials;
}

function getTransactionOfficials(transactionsList, originalCompanyOfficials) {
  if (transactionsList.length > 0) {
    const individualOfficials = getIndividualOfficials(transactionsList);
    const corporateOfficials = getCorporateOfficials(transactionsList);
    const shareCapitalChangesOfficials = getShareCapitalChangesOfficials(
      transactionsList,
      originalCompanyOfficials
    );
    const uniqueTransactionOfficials = [];
    const seenIds = new Set();
    [
      ...individualOfficials,
      ...corporateOfficials,
      ...shareCapitalChangesOfficials,
    ].forEach((official) => {
      if (!seenIds.has(official?._id)) {
        seenIds.add(official._id);
        uniqueTransactionOfficials.push(official);
      }
    });
    return uniqueTransactionOfficials;
  } else {
    return [];
  }
}

function getIndividualOfficials(transactionsList) {
  let individualOfficials = [];
  transactionsList.forEach((trx) => {
    if (trx?.individualOfficials?.length > 0) {
      trx.individualOfficials.forEach((official) => {
        if (official?._id) {
          const updatedOfficial = {
            ...official,
            transaction: trx?._id,
            designation: official.designation || "Official",
            fullName: getFullName(official),
          };
          individualOfficials.push(updatedOfficial);
        }
      });
    }
  });
  return individualOfficials;
}

function getCorporateOfficials(transactionsList) {
  let corporateOfficials = [];
  transactionsList.forEach((trx) => {
    if (trx?.corporateOfficials?.length > 0) {
      trx.corporateOfficials.forEach((official) => {
        if (official?._id) {
          const updatedOfficial = {
            ...official,
            transaction: trx?._id,
            designation: official.designation || "Corporate Official",
            fullName: getFullName(official),
          };
          corporateOfficials.push(updatedOfficial);
        }
      });
    }
  });
  return corporateOfficials;
}

function getShareCapitalChangesOfficials(
  transactionsList,
  originalCompanyOfficials
) {
  let shareCapitalChangesOfficials = [];
  transactionsList.forEach((trx) => {
    if (trx?.shareCapitalChanges?.length > 0) {
      trx.shareCapitalChanges.forEach((shareChange) => {
        if (shareChange?.allocations?.length > 0) {
          shareChange.allocations.forEach((allocation) => {
            if (allocation?.officialId) {
              const matchingOfficial = originalCompanyOfficials.find(
                (co) => co?._id === allocation.officialId
              );

              const remainder = trx?.shareCapitalChanges?.[0]?.remainder || 0;
              const updatedOfficial = {
                _id: allocation.officialId,
                ...matchingOfficial,
                fullName: matchingOfficial
                  ? getFullName(matchingOfficial)
                  : allocation.name ||
                    matchingOfficial?.names?.companyName ||
                    "Unknown Official",
                designation: matchingOfficial?.designation,
                idType: matchingOfficial?.idType,
                remainder: remainder,
                currentShares: allocation.currentShares || 0,
                allocatedShares: allocation.allocatedShares || 0,
                proportion: allocation.proportion || "0",
                transaction: trx?._id,
                classOfShares: shareChange.classOfShares || "",
                changeType: shareChange.changeType || "",
                offerPricePerShare: shareChange.offerPricePerShare || 0,
                premiumPerShare: shareChange.premiumPerShare || 0,
                dateOfOffer: shareChange.dateOfOffer || null,
                AuthorizationId: shareChange.AuthorizationId || null,
                allotmentId: shareChange.allotmentId || null,
              };
              shareCapitalChangesOfficials.push(updatedOfficial);
            }
          });
        }
      });
    }
  });
  return shareCapitalChangesOfficials;
}
